import type { CalendarEventGroupAnchorMode, PeopleTravelOptionKind } from '../types';
import { auth } from './firebase';
import {
  TRAVEL_AGENT_ENDPOINT,
  TRAVEL_CALENDAR_SYNC_POLL_TIMEOUT_MS,
  TRAVEL_CALENDAR_SYNC_REQUESTS,
  TRAVEL_SEARCH_POLL_TIMEOUT_MS,
  TRAVEL_SEARCH_REQUESTS,
} from './travelAgentGuide';
import type { EventGroupSegmentPayload } from './executiveCalendarDelegatedService';

interface RequestOutcome {
  status: 'complete' | 'failed';
  error?: string;
}

async function getStaffIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user || user.isAnonymous) {
    throw new Error('Sign in required');
  }
  return user.getIdToken();
}

async function postTravelAgent(body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const token = await getStaffIdToken();
  const response = await fetch(TRAVEL_AGENT_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.success === false) {
    throw new Error(data?.error || `Travel agent request failed (HTTP ${response.status})`);
  }
  return data as Record<string, unknown>;
}

export async function requestTripTravelSearch(
  tripId: string,
  kinds: PeopleTravelOptionKind[] = ['flight', 'hotel', 'rental_car'],
  preferences?: Record<string, unknown>,
): Promise<void> {
  await postTravelAgent({
    action: 'requestTripTravelSearch',
    tripId,
    kinds,
    preferences,
  });
}

export async function requestTripCalendarSync(
  tripId: string,
  params: {
    executiveUid: string;
    invitees: string[];
    anchorMode?: CalendarEventGroupAnchorMode;
    timeZone?: string;
    segments: EventGroupSegmentPayload[];
  },
): Promise<void> {
  await postTravelAgent({
    action: 'requestTripCalendarSync',
    tripId,
    executiveUid: params.executiveUid,
    invitees: params.invitees,
    anchorMode: params.anchorMode || 'activity_start',
    timeZone: params.timeZone,
    segments: params.segments,
  });
}

function waitForRequest(
  collection: string,
  docId: string,
  timeoutMs: number,
  timeoutMessage: string,
): Promise<RequestOutcome> {
  return new Promise((resolve, reject) => {
    let settled = false;
    let unsubscribe: (() => void) | null = null;

    const finish = (outcome: RequestOutcome) => {
      if (settled) return;
      settled = true;
      unsubscribe?.();
      clearTimeout(timer);
      resolve(outcome);
    };

    const fail = (err: Error) => {
      if (settled) return;
      settled = true;
      unsubscribe?.();
      clearTimeout(timer);
      reject(err);
    };

    const timer = setTimeout(() => {
      fail(new Error(timeoutMessage));
    }, timeoutMs);

    import('firebase/firestore')
      .then(({ doc, onSnapshot }) =>
        import('./firebase').then(({ db }) => {
          unsubscribe = onSnapshot(
            doc(db, collection, docId),
            snap => {
              if (!snap.exists()) return;
              const status = snap.data()?.status;
              if (status === 'complete') finish({ status: 'complete' });
              else if (status === 'failed') {
                finish({
                  status: 'failed',
                  error: (snap.data()?.error as string) || 'Request failed',
                });
              }
            },
            err => fail(err instanceof Error ? err : new Error(String(err))),
          );
        }),
      )
      .catch(err => fail(err instanceof Error ? err : new Error(String(err))));
  });
}

export function waitForTripTravelSearch(
  tripId: string,
  timeoutMs = TRAVEL_SEARCH_POLL_TIMEOUT_MS,
): Promise<RequestOutcome> {
  return waitForRequest(
    TRAVEL_SEARCH_REQUESTS,
    tripId,
    timeoutMs,
    'Travel search timed out — ensure the Mac mini travel agent is running',
  );
}

export function waitForTripCalendarSync(
  tripId: string,
  timeoutMs = TRAVEL_CALENDAR_SYNC_POLL_TIMEOUT_MS,
): Promise<RequestOutcome> {
  return waitForRequest(
    TRAVEL_CALENDAR_SYNC_REQUESTS,
    tripId,
    timeoutMs,
    'Calendar sync timed out — ensure the Calendar Agent is running',
  );
}

export function subscribeTravelSearchRequest(
  tripId: string,
  onData: (data: Record<string, unknown> | null) => void,
): () => void {
  let unsub: (() => void) | null = null;
  import('firebase/firestore')
    .then(({ doc, onSnapshot }) =>
      import('./firebase').then(({ db }) => {
        unsub = onSnapshot(doc(db, TRAVEL_SEARCH_REQUESTS, tripId), snap => {
          onData(snap.exists() ? (snap.data() as Record<string, unknown>) : null);
        });
      }),
    )
    .catch(() => onData(null));
  return () => unsub?.();
}

export function subscribeCalendarSyncRequest(
  tripId: string,
  onData: (data: Record<string, unknown> | null) => void,
): () => void {
  let unsub: (() => void) | null = null;
  import('firebase/firestore')
    .then(({ doc, onSnapshot }) =>
      import('./firebase').then(({ db }) => {
        unsub = onSnapshot(doc(db, TRAVEL_CALENDAR_SYNC_REQUESTS, tripId), snap => {
          onData(snap.exists() ? (snap.data() as Record<string, unknown>) : null);
        });
      }),
    )
    .catch(() => onData(null));
  return () => unsub?.();
}
